/**
 * Store offres + popup — Phase 0.
 *
 * En attendant le backend, l'admin peut surcharger les offres et la config
 * popup via localStorage. La structure est strictement la même que celle
 * de `catalog.js > OFFERS` : la migration vers /api/offers se fera sans
 * changement côté composants.
 */

import { OFFERS, POPUP_DEFAULTS } from "@/data/catalog";

const LS_OFFERS = "le11.offers.v1";
const LS_POPUP_CFG = "le11.popup.cfg.v1";
const LS_POPUP_SEEN = "le11.popup.seen.v1";

/* ---------- Offres ---------- */

export const getOffers = () => {
  try {
    const raw = localStorage.getItem(LS_OFFERS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    /* ignore */
  }
  // Defaults — clone pour éviter mutation accidentelle
  return JSON.parse(JSON.stringify(OFFERS.items));
};

export const saveOffers = (offers) => {
  localStorage.setItem(LS_OFFERS, JSON.stringify(offers));
};

export const resetOffers = () => {
  localStorage.removeItem(LS_OFFERS);
};

/* ---------- Popup config ---------- */

export const getPopupConfig = () => {
  try {
    const raw = localStorage.getItem(LS_POPUP_CFG);
    if (raw) return { ...POPUP_DEFAULTS, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { ...POPUP_DEFAULTS };
};

export const savePopupConfig = (cfg) => {
  localStorage.setItem(LS_POPUP_CFG, JSON.stringify(cfg));
};

/* ---------- Disponibilité d'une offre ---------- */

/**
 * Renvoie true si l'offre est valide MAINTENANT en fonction de :
 *  - active
 *  - start_date / end_date
 *  - active_days (1=lundi … 7=dimanche)
 *  - start_time / end_time
 */
export const isOfferLive = (offer, now = new Date()) => {
  if (!offer || !offer.active) return false;

  // Dates
  const today = now.toISOString().slice(0, 10);
  if (offer.start_date && today < offer.start_date) return false;
  if (offer.end_date && today > offer.end_date) return false;

  // Jours actifs (1=Mon…7=Sun)
  if (Array.isArray(offer.active_days) && offer.active_days.length > 0) {
    const jsDay = now.getDay(); // 0=Dim…6=Sam
    const dayIso = jsDay === 0 ? 7 : jsDay;
    if (!offer.active_days.includes(dayIso)) return false;
  }

  // Horaires
  if (offer.start_time && offer.end_time) {
    const [sH, sM] = offer.start_time.split(":").map(Number);
    const [eH, eM] = offer.end_time.split(":").map(Number);
    const cur = now.getHours() * 60 + now.getMinutes();
    const start = sH * 60 + sM;
    const end = eH * 60 + eM;
    // Cas simple sans franchissement de minuit
    if (start <= end) {
      if (cur < start || cur > end) return false;
    } else {
      // Plage qui passe minuit (ex 11:00 – 03:00)
      if (cur < start && cur > end) return false;
    }
  }

  return true;
};

/* ---------- Popup "déjà vu" ---------- */

const getSeen = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_POPUP_SEEN) || "{}");
  } catch {
    return {};
  }
};

export const wasPopupSeen = (offerId, frequency) => {
  const seen = getSeen();
  const entry = seen[offerId];
  if (!entry) return false;
  if (frequency === "once_per_session") {
    // sessionStorage flag — séparé de localStorage
    return sessionStorage.getItem(`le11.popup.session.${offerId}`) === "1";
  }
  // once_per_day par défaut
  return entry === new Date().toISOString().slice(0, 10);
};

export const markPopupSeen = (offerId, frequency) => {
  if (frequency === "once_per_session") {
    sessionStorage.setItem(`le11.popup.session.${offerId}`, "1");
    return;
  }
  const seen = getSeen();
  seen[offerId] = new Date().toISOString().slice(0, 10);
  localStorage.setItem(LS_POPUP_SEEN, JSON.stringify(seen));
};

export const resetPopupSeen = (offerId) => {
  const seen = getSeen();
  delete seen[offerId];
  localStorage.setItem(LS_POPUP_SEEN, JSON.stringify(seen));
  sessionStorage.removeItem(`le11.popup.session.${offerId}`);
};

/** Vide complètement le suivi "déjà vu" (popup) — toutes offres confondues. */
export const resetAllPopupSeen = () => {
  try {
    localStorage.removeItem(LS_POPUP_SEEN);
    // Sweep sessionStorage entries
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith("le11.popup.session.")) toRemove.push(k);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
};
